import { Extension, Node, mergeAttributes } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

interface AttachmentChipAttrs {
  id: string
  name: string
}

/**
 * Rendered as a plain `<span data-attachment-id>` rather than a `<a href>` —
 * an attachment's URL is often a short-lived signed S3 link, so the node
 * stores only the durable attachment id and lets the viewer resolve today's
 * URL against the entity's live attachment list at render time.
 */
const AttachmentChip = Node.create({
  name: "attachmentChip",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-attachment-id"),
        renderHTML: (attrs: AttachmentChipAttrs) => ({
          "data-attachment-id": attrs.id
        })
      },
      name: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-attachment-name"),
        renderHTML: (attrs: AttachmentChipAttrs) => ({
          "data-attachment-name": attrs.name
        })
      }
    }
  },

  parseHTML() {
    return [{ tag: "span[data-attachment-id]" }]
  },

  renderHTML({ HTMLAttributes, node }) {
    // No `class` — the sanitizer strips it from saved HTML (see
    // rich-text-html.ts), so the chip's look is keyed off
    // `[data-attachment-id]` in CSS instead, which survives sanitizing.
    return [
      "span",
      mergeAttributes(HTMLAttributes, { contenteditable: "false" }),
      `📎 ${node.attrs.name}`
    ]
  }
})

/** Tab/Shift-Tab nest and un-nest a list item — the only "indent" a compact
 * note editor offers, and StarterKit's ListItem binds neither by default. */
const ListIndentKeymap = Extension.create({
  name: "listIndentKeymap",
  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.sinkListItem("listItem"),
      "Shift-Tab": () => this.editor.commands.liftListItem("listItem")
    }
  }
})

// Matches the server's own reading: case-insensitive, any run of spaces.
const normalizeToken = (token: string) =>
  token.trim().toUpperCase().replace(/\s+/g, " ")

const TOKEN_HIGHLIGHT_KEY = new PluginKey("tokenHighlight")
const BRACKETED = /\[[^[\]\n]{1,60}\]/g

/**
 * Shows each personalization field in the body bold, whether picked from the
 * chips, typed or already saved. A decoration only — the saved HTML and the
 * email keep exactly what the author wrote.
 */
const TokenHighlight = Extension.create<{ getTokens: () => readonly string[] }>(
  {
    name: "tokenHighlight",
    addOptions() {
      return { getTokens: () => [] }
    },
    addProseMirrorPlugins() {
      const { getTokens } = this.options
      return [
        new Plugin({
          key: TOKEN_HIGHLIGHT_KEY,
          props: {
            decorations(state) {
              const known = new Set(getTokens().map(normalizeToken))
              if (!known.size) return null
              const decorations: Decoration[] = []
              state.doc.descendants((node, pos) => {
                if (!node.isText || !node.text) return
                for (const match of node.text.matchAll(BRACKETED)) {
                  if (!known.has(normalizeToken(match[0]))) continue
                  const from = pos + (match.index ?? 0)
                  decorations.push(
                    Decoration.inline(from, from + match[0].length, {
                      class: "rte-token"
                    })
                  )
                }
              })
              return DecorationSet.create(state.doc, decorations)
            }
          }
        })
      ]
    }
  }
)

export {
  type AttachmentChipAttrs,
  AttachmentChip,
  ListIndentKeymap,
  TOKEN_HIGHLIGHT_KEY,
  TokenHighlight
}
